<script setup lang="ts">
import {ref, computed} from 'vue';
import {useMessagesStore, useLocalizationStore, useSessionStore} from 'src/store';
import {type MessageModel, type MessageSource, type ExternalSource} from 'src/types/interfaces';
import {toUserFacingDate} from 'src/utils/dateUtil';
import {formatName} from '@/utils/interfaceUtils';
import Avatar from '@/components/Avatar.vue';
import {useI18n} from 'vue-i18n';
import MadeByAIBadge from '@/components/MadeByAIBadge.vue';
import EPubModal from '@/components/EPubModal.vue';
import {sendAIFeedback} from '@/api/endpoints/messagesWeb.ts';
import {handleApiRequest} from '@/api/handleApiRequest.ts';
import {bus} from '@/utils/eventBus';
import BtnStandard from '@/components/BtnStandard.vue';

const {t} = useI18n();

const props = defineProps<{
  message: MessageModel;
  isClinicianMsg: boolean;
  isAssistantMsg: boolean;
  maEnabled: boolean;
  maPollInterval?: number;
  onRetry?: (message: MessageModel) => void;
}>();

const isPending = computed(() => props.message._status === 'pending');
const isFailed = computed(() => props.message._status === 'failed');

const messagesModule = useMessagesStore();
const sessionModule = useSessionStore();
const localizationModule = useLocalizationStore();

const responseBody = ref('');

const showContentModal = ref(false);

const feedbackProcessing = ref(false)

const createdDate = computed(() => toUserFacingDate(props.message.createdDate, true, localizationModule.locale));

const avatar = computed(() => {

  let avatar: string | undefined | null = null;
  if (props.isClinicianMsg) {
    if (props.isAssistantMsg) {
      avatar = 'img/edBotAvi.svg';
    } else if (!messagesModule.careTeam) {
      return null;
    } else {
      avatar = messagesModule.getAvatarforClinicianUserId(props.message.fromId.toString());
    }
  } else {
    avatar = sessionModule.user?.avatarBase64;
  }

  if (!avatar) return null;
  else if (props.isAssistantMsg) {
    return avatar;
  } else {
    return `data:image/png;base64,${avatar}`;
  }

});

// An incision check-in receipt: the API already composed the body in the patient's language
const isCheckinReceipt = computed(() => props.message.type === 'CHECKIN_COMPLETION_FEEDBACK');

const showReceiptPhoto = ref(true);

const appendAIBadge = computed(() => {
  return props.isAssistantMsg && props.message.type == 'ASSISTANT_REPLY';
})

const appendFeedback = computed(() => {
  return props.isAssistantMsg && props.message.type == 'ASSISTANT_FEEDBACK_REQUEST';
})

const formatedMessage = computed(() => {
  const message = clearAnchorTagGetUrl(props.message.message);
  return [message];
});

const name = computed(() => {
  if (!props.isAssistantMsg) {
    return formatName({firstName: props.message.fromFirstName, lastName: props.message.fromLastName});
  } else {
    return props.message.fromFirstName
  }

});

const hasSources = computed(() => {
  return props.message?.sources && props.message.sources.length > 0;
})

const validExternalSources = computed(() => {
  return (props.message?.externalSources ?? [])
    .filter(s => {
      if (!s.title?.trim() || !s.url?.trim()) return false;
      try {
        const u = new URL(s.url, window.location.origin);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    });
});

const hasAnySources = computed(() => {
  return (props.message?.sources?.length > 0) ||
         (validExternalSources.value.length > 0);
});

function clearAnchorTagGetUrl(message: string): string {
  const anchorOpen = /<a.*?>/g,
    anchorClose = /<\/a>/g,
    url = /(http:\/\/|https:\/\/)([\S]+)/g,
    str = message.replace(anchorOpen, '').replace(anchorClose, '');

  const result: string = str.replace(url, formatLink);
  return result;
}

function formatLink(match: string) {
  if (/[.!?]$/.test(match)) {
    const urlWithMark = /(http:\/\/|https:\/\/)([\S]+)([.!?]$)/g;
    return match.replace(urlWithMark, '<a href="$1$2" target="_blank">$1$2</a>$3');
  }
  return '<a href="' + match + '" target="_blank">' + match + '</a>';
}

async function clickHandler() {
  responseBody.value = '/Asset/GetMessageAttachment?messageId=' + props.message.messageId + '&dataId=' + props.message.dataId;
  showContentModal.value = true;
}

function openReceiptPhoto() {
  responseBody.value = props.message.mediaUrl + '&thumbnail=false';
  showContentModal.value = true;
}

async function handleFeedback(help: boolean, guid: string) {
  feedbackProcessing.value = true;
  const response = await handleApiRequest(() => sendAIFeedback(help, sessionModule?.user.selectedEocId, guid), t('gen_error_feedback'));
  if (response.result?.feedbackResponse) {
    if (window._paq) {
      window._paq.push(['trackEvent', 'Message-Assistant', 'Feedback', 'Usefulness', help ? 'Yes' : 'No']);
    }
    feedbackProcessing.value = false;
    bus.emit('on-bot-resolved')
  }else {
    feedbackProcessing.value = false;
  }
}
const showEpubModal = ref(false);
const ePubUrl = ref();
function modalPrep (source: MessageSource): void {
  ePubUrl.value = source.url;
  showEpubModal.value = true;
}

</script>

<template>
  <!--  incision check-in receipt -->
  <div v-if="isCheckinReceipt" class="not-ai checkin-receipt">
    <q-chat-message data-testid="checkinReceipt">
      
      <!-- Avatar -->
      <template #avatar>
        <Avatar color="primitives-foundation-50"
                :has-avatar-image="false"
                :icon-border="false"
                icon-font-size="17px"
                icon-name="fa-regular fa-bandage fa-rotate-by"
                size="42px"
                style="--fa-rotate-angle: -45deg;" />
      </template>
  
      <!-- Text -->
      <div v-if="message.message != ''" 
           v-html="message.message" />
      
      <!-- Date -->
      <div>{{ createdDate }}</div>
  
      <!-- Thumbnail -->
      <img v-if="message.mediaUrl && showReceiptPhoto"
           @click="openReceiptPhoto()"
           @error="showReceiptPhoto = false"
           @keydown.enter.prevent="openReceiptPhoto()"
           @keydown.space.prevent="openReceiptPhoto()"
           alt=""
           data-testid="checkinReceiptPhoto"
           role="button"
           :src="message.mediaUrl"
           tabindex="0" />
    </q-chat-message>
  </div>

  <!--  ai messages -->
  <div v-else-if="isAssistantMsg" class="message-assist message-assist_wrapper">
    <q-chat-message :name="createdDate" :sent="!isClinicianMsg" data-testid="message" :data-test2="isClinicianMsg">

      <template v-if="isAssistantMsg" #label>
        {{ t('chatbot_name_message_assistant') }}
      </template>

      <template v-else #label>
        {{ name }}
      </template>

      <!-- Avatar -->
      <template #avatar>
        <Avatar :avatar-url="avatar !== null ? avatar : undefined" color="secondary" :display-name="name"
                :has-avatar-image="avatar !== null" :icon-border="isAssistantMsg ? false : true"
                :icon-font-size="(isClinicianMsg || isAssistantMsg) ? '24px' : '34px'"
                :icon-name="'fa-solid ' + (isClinicianMsg ? 'fa-user-md' : 'fa-user-alt')"
                :icon-top-margin="isClinicianMsg ? '' : '5px'" :isAIavi="isAssistantMsg"
                :size="isAssistantMsg ? '60px': '42px' "/>
      </template>
      <!-- Text -->
      <p v-if="message.message != ''" v-html="formatedMessage"/>

      <div v-if="hasAnySources" class="sources wrapper q-pb-sm">
        <span class="sources subheader">{{ t('message_assistant_sources') }}</span>

        <div v-for="source in message.sources" :key="source.assetId" class="source item q-pb-lg">
          <span @click="modalPrep(source)" class="source-title" :title="source.localizedTitle">{{ source.localizedTitle }}</span>
        </div>
        <e-pub-modal :ePubUrl="ePubUrl" v-model="showEpubModal"/>

        <div v-for="source in validExternalSources" :key="source.url" class="source item q-pb-lg">
          <a :href="source.url"
             target="_blank"
             rel="noopener noreferrer"
             class="external-source-link"
             :title="source.title">
            <span class="external-source-title">{{ source.title }}</span>
            <q-icon name="fal fa-arrow-up-right-from-square" size="12px" aria-hidden="true" />
          </a>
        </div>
      </div>

      <made-by-a-i-badge v-if="appendAIBadge"></made-by-a-i-badge>

      <div v-if="appendFeedback" class="row feedback">
        <div class="col-4">
          <btn-standard @click="handleFeedback(false, message?.conversationGuid)" :label="t('gen_yes')"
                        :type="$zb.enums.ButtonTypeEnum.button" :rounded="true" :outline="true"
                        :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                        :disable="message.isResponseHelpful != null || !props.maEnabled || feedbackProcessing" data-testid="AIfeedbackYes"
                        :class="[{'selected': message.isResponseHelpful == false}]"/>
        </div>
        <div class="col-4 q-mx-md">
          <btn-standard @click="handleFeedback(true, message?.conversationGuid)" :label="t('gen_no')"
                        :type="$zb.enums.ButtonTypeEnum.button" :rounded=" true" :outline="true"
                        :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                        :disable="message.isResponseHelpful != null || !props.maEnabled || feedbackProcessing" data-testid="AIfeedbackNo"
                        :class="[{'selected':  message.isResponseHelpful == true}] "/>

        </div>


      </div>
      <!-- Image -->
      <div v-if="message.mediaContentType?.match('image')" @click="clickHandler()" class="media"
           :data-url="message.mediaUrl">
        <q-icon name="fa-solid fa-image" size="24px"/>
      </div>

      <!-- Video -->
      <div v-if="message.mediaContentType?.match('video')" @click="clickHandler()" class="media" data-url="URL">
        <q-icon name="fa-solid fa-video" size="24px"/>
      </div>
    </q-chat-message>
  </div>


  <!--  care team or patient message-->
  <div v-else class="not-ai" :class="{ 'msg-pending': isPending, 'msg-failed': isFailed }">
    <q-chat-message :name="isFailed ? undefined : createdDate" :sent="!isClinicianMsg">

      <template v-if="isClinicianMsg" #label>
        {{ name }}
      </template>

      <template v-if="isFailed" #name>
        <span class="msg-retry" @click.stop="onRetry?.(message)">
          <q-icon name="fa-solid fa-circle-exclamation" color="negative" size="14px" />
          {{ t('gen_not_delivered', 'Not delivered. Tap to retry.') }}
        </span>
      </template>

      <!-- Avatar -->
      <template #avatar>
        <Avatar v-if="isClinicianMsg" :avatar-url="avatar !== null ? avatar : undefined" color="secondary"
                :display-name="name" :has-avatar-image="avatar !== null" :icon-border="true"
                :icon-font-size="(isClinicianMsg || isAssistantMsg) ? '24px' : '34px'"
                :icon-name="'fa-solid ' + (isClinicianMsg ? 'fa-user-md' : 'fa-user-alt')"
                :icon-top-margin="isClinicianMsg ? '' : '5px'" size="42px"/>
      </template>
      <!-- Text -->
      <p v-if="message.message != ''" v-html="formatedMessage" data-testid="messageText"/>

      <!-- Image -->
      <div v-if="message.mediaContentType?.match('image')" @click="clickHandler()" class="media"
           :data-url="message.mediaUrl" data-testid="image">
        <q-icon name="fa-solid fa-image" size="24px"/>
      </div>

      <!-- Video -->
      <div v-if="message.mediaContentType?.match('video')" @click="clickHandler()" class="media" data-url="URL"
           data-testid="video">
        <q-icon name="fa-solid fa-video" size="24px"/>
      </div>
    </q-chat-message>
  </div>

  <zbm-dialog v-if="message.mediaUrl" v-model="showContentModal" :close-button="true" data-testid="mediaModal">

    <!-- .jpg/.jpeg Image -->
    <img v-if="message.mediaContentType == 'image/jpeg'" :src="responseBody"/>

    <!-- .png Image -->
    <img v-if="message.mediaContentType == 'image/png'" :src="responseBody"/>

    <!-- Video -->
    <video v-if="message.mediaContentType == 'video/mp4'" controls controlslist="nodownload nofullscreen" disablepictureinpicture>
      <source :src="responseBody" type="video/mp4"/>
    </video>
  </zbm-dialog>
</template>

<style lang="scss" scoped>
@use 'sass:map';

// Check-in receipt: the CWE layout - full width, rules above and below, icon on the left
.checkin-receipt {
  
  .q-message {
    padding-top: 22px;
    padding-bottom: 22px;
    border-block: 1px solid map.get($co-brand-clrs, primitives-foundation-10);
    
    &.q-message-received {
      
      // Text
      :deep(.q-message-text) {
        background-color: $white;
        line-height: map.get($line-heights, '24');
        padding: 0;
        padding-block-start: 8px;
        border-radius: 0;
        margin-block-end: 0;

        p,
        ul {
          margin-block-end: 0;
        }
        
        // Date
        + .q-message-text {
          line-height: normal;
          padding-block-start: 0;
          margin-block-start: 6px;
          
          // Thumbnail
          + .q-message-text {
            line-height: 0;
            margin-block-start: 16px;
            
            img {
              width: 52px;
              height: 52px;
              border: 1px solid #c7c7cc;
              border-radius: $generic-border-radius-md;
              cursor: pointer;
            }
          }
        }
      }

      :deep(.q-message-text--received) {
        
        + .q-message-text--received {

          .q-message-text-content--received {
            color: map.get($co-brand-clrs, primitives-foundation-50);
          }
        }
      }
    }
  }

  + .checkin-receipt {
    
    .q-message {
      padding-top: 11px;
      border-block-start: 0;
    }
  }
}

.message-assist {
  &.message-assist {
    &_wrapper {
      background: linear-gradient(179deg, #EBE3F0 0.64%, rgba(235, 227, 240, 0.00) 98.14%);

      :deep(.q-message-text.q-message-text--received) {
        background: transparent;
      }
    }
  }
  .sources.subheader {
    font-weight: 600;
    font-size: 13px;
    color: $content-txt-secondary;
  }

  // Source titles can be long (full document names / external URLs). Wrap and
  // break them so the whole value stays visible — never let a non-wrapping run
  // widen the bubble, which would force the chat thread to scroll horizontally.
  .source .source-title {
    overflow-wrap: anywhere;
    text-decoration: underline;
    cursor: pointer;
  }

  .external-source-link {
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
    overflow-wrap: anywhere;

    &:hover {
      text-decoration-thickness: 2px;
    }

    .q-icon {
      font-size: 12px;
      margin-inline-start: 6px;
    }

    .external-source-title {
      overflow-wrap: anywhere;
    }
  }
}

.msg-pending {
  opacity: 0.5;
}

.msg-failed {
  :deep(.q-message-text) {
    opacity: 0.6;
  }
}

.msg-retry {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: var(--q-negative, #c10015);
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.q-dialog {

  // .jpg/.jpeg/.png Image & Video
  img,
  video {
    max-width: 700px;
    width: 100%;
    max-height: 700px;
  }

  // .jpg/.jpeg/.png Image
  img {
    height: auto;
  }
}
</style>
